<script>
/* eslint-disable vue/html-closing-bracket-newline */
import GlobalArticlesAPI from '../../../api/globalArticles';

export default {
  name: 'HelpCenterViewer',
  data() {
    return {
      articles: [],
      selectedArticle: null,
      articlesLoading: true,
      articleLoading: false,
      errorMessage: '',
      searchQuery: '',
    };
  },
  computed: {
    filteredArticles() {
      if (!this.searchQuery) return this.articles;
      const normalizedQuery = this.normalizeSearchText(this.searchQuery);
      return this.articles.filter(article => {
        return (
          this.normalizeSearchText(article.title).includes(normalizedQuery) ||
          this.normalizeSearchText(this.stripHtml(article.content)).includes(
            normalizedQuery
          )
        );
      });
    },
    groupedArticles() {
      const groups = {};
      this.articles.forEach(article => {
        const tag =
          this.parsedTag(article.title) ||
          this.$t('HELP_CENTER_VIEWER.GENERAL');
        if (!groups[tag]) groups[tag] = [];
        groups[tag].push(article);
      });
      return groups;
    },
  },
  mounted() {
    this.fetchArticles();
  },
  methods: {
    parsedTag(title) {
      const match = title.match(/^\[(.*?)\]/);
      return match ? match[1] : null;
    },
    cleanTitle(title) {
      return title.replace(/^\[.*?\]\s*/, '');
    },
    normalizeSearchText(value = '') {
      return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase(this.$i18n.locale)
        .trim();
    },
    stripHtml(value = '') {
      const documentFragment = new DOMParser().parseFromString(
        value,
        'text/html'
      );
      return documentFragment.body.textContent || '';
    },
    async fetchArticles() {
      this.articlesLoading = true;
      this.errorMessage = '';
      try {
        const response = await GlobalArticlesAPI.getArticles();
        this.articles = response.data;
        const requestedArticle = new URLSearchParams(
          window.location.search
        ).get('article');
        if (requestedArticle === 'direct-send') {
          const directSendArticle = this.articles.find(article =>
            article.title.toLowerCase().includes('direct send')
          );
          if (directSendArticle) await this.selectArticle(directSendArticle.id);
        }
      } catch (error) {
        this.errorMessage = this.$t('HELP_CENTER_VIEWER.ERRORS.LOAD_ARTICLES');
      } finally {
        this.articlesLoading = false;
      }
    },
    async selectArticle(id) {
      this.articleLoading = true;
      this.errorMessage = '';
      try {
        const response = await GlobalArticlesAPI.getArticle(id);
        this.selectedArticle = response.data;
        this.$nextTick(() => this.$refs.articleTitle?.focus());
      } catch (error) {
        this.errorMessage = this.$t('HELP_CENTER_VIEWER.ERRORS.OPEN_ARTICLE');
      } finally {
        this.articleLoading = false;
      }
    },
    openSupportChat() {
      if (window.$lumeratec?.toggle) {
        window.$lumeratec.toggle();
        return;
      }

      this.errorMessage = this.$t(
        'HELP_CENTER_VIEWER.ERRORS.SUPPORT_UNAVAILABLE'
      );
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col h-full bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 overflow-hidden w-full font-sans transition-colors duration-200"
  >
    <header
      class="flex flex-col px-8 pt-8 pb-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm z-10"
    >
      <div class="flex justify-between items-center">
        <div>
          <h1
            class="text-2xl font-semibold text-slate-800 dark:text-white tracking-tight"
          >
            {{ $t('HELP_CENTER_VIEWER.TITLE') }}
          </h1>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {{ $t('HELP_CENTER_VIEWER.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center gap-4">
          <div class="relative flex items-center shrink-0">
            <span
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-slate-400"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <label for="help-center-search" class="sr-only">
              {{ $t('HELP_CENTER_VIEWER.SEARCH_LABEL') }}
            </label>
            <input
              id="help-center-search"
              v-model="searchQuery"
              type="text"
              :placeholder="$t('HELP_CENTER_VIEWER.SEARCH_PLACEHOLDER')"
              :aria-label="$t('HELP_CENTER_VIEWER.SEARCH_LABEL')"
              class="w-72 !pl-10 !pr-4 !py-2 !m-0 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-slate-800 dark:text-slate-200 placeholder-slate-400"
            />
          </div>
        </div>
      </div>
    </header>

    <div
      v-if="errorMessage"
      role="alert"
      class="flex items-center justify-between gap-4 px-8 py-3 text-sm text-red-800 bg-red-50 border-b border-red-200 dark:text-red-200 dark:bg-red-950/40 dark:border-red-900"
    >
      <span>{{ errorMessage }}</span>
      <button class="font-medium underline" @click="fetchArticles">
        {{ $t('HELP_CENTER_VIEWER.RETRY') }}
      </button>
    </div>

    <div class="flex flex-1 overflow-hidden">
      <!-- Left Sidebar: Articles List -->
      <nav
        :aria-label="$t('HELP_CENTER_VIEWER.ARTICLES_LABEL')"
        class="w-[350px] border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col h-full"
      >
        <div class="p-4 border-b border-slate-100 dark:border-slate-700/50">
          <h3
            class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
          >
            {{ $t('HELP_CENTER_VIEWER.CATEGORIES_AND_ARTICLES') }}
          </h3>
        </div>

        <div class="flex-1 overflow-y-auto p-3">
          <div
            v-if="articlesLoading"
            class="flex justify-center p-8"
            role="status"
            :aria-label="$t('HELP_CENTER_VIEWER.LOADING_ARTICLES')"
          >
            <span
              class="icon i-lucide-loader animate-spin text-2xl text-slate-400"
            />
          </div>

          <div
            v-else-if="filteredArticles.length === 0"
            class="flex flex-col items-center justify-center p-8 text-center"
          >
            <div
              class="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3"
            >
              <span class="icon i-lucide-search text-xl text-slate-400" />
            </div>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ $t('HELP_CENTER_VIEWER.NO_ARTICLES') }}
            </p>
            <button
              v-if="searchQuery"
              class="text-xs text-blue-500 mt-2"
              @click="searchQuery = ''"
            >
              {{ $t('HELP_CENTER_VIEWER.CLEAR_SEARCH') }}
            </button>
          </div>

          <div v-else class="space-y-4">
            <template v-if="!searchQuery">
              <div
                v-for="(groupArticles, category) in groupedArticles"
                :key="category"
              >
                <h4
                  class="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 px-2"
                >
                  {{ category }}
                </h4>
                <ul class="space-y-1">
                  <li v-for="article in groupArticles" :key="article.id">
                    <button
                      type="button"
                      class="group w-full p-3 rounded-xl cursor-pointer transition-all border shadow-sm text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
                      :class="[
                        selectedArticle?.id === article.id
                          ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800'
                          : 'bg-white border-slate-200 hover:border-blue-300 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600',
                      ]"
                      :aria-current="
                        selectedArticle?.id === article.id ? 'page' : undefined
                      "
                      @click="selectArticle(article.id)"
                    >
                      <span
                        class="font-medium text-sm line-clamp-2"
                        :class="
                          selectedArticle?.id === article.id
                            ? 'text-blue-800 dark:text-blue-300'
                            : 'text-slate-800 dark:text-slate-200'
                        "
                        >{{ cleanTitle(article.title) }}</span
                      >
                    </button>
                  </li>
                </ul>
              </div>
            </template>

            <ul v-else class="space-y-1">
              <li v-for="article in filteredArticles" :key="article.id">
                <button
                  type="button"
                  class="group w-full p-3 rounded-xl cursor-pointer transition-all border shadow-sm text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
                  :class="[
                    selectedArticle?.id === article.id
                      ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800'
                      : 'bg-white border-slate-200 hover:border-blue-300 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600',
                  ]"
                  :aria-current="
                    selectedArticle?.id === article.id ? 'page' : undefined
                  "
                  @click="selectArticle(article.id)"
                >
                  <span v-if="parsedTag(article.title)" class="block mb-1">
                    <span
                      class="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase"
                      >{{ parsedTag(article.title) }}</span
                    >
                  </span>
                  <span
                    class="block font-medium text-sm line-clamp-2"
                    :class="
                      selectedArticle?.id === article.id
                        ? 'text-blue-800 dark:text-blue-300'
                        : 'text-slate-800 dark:text-slate-200'
                    "
                  >
                    {{ cleanTitle(article.title) }}
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      <!-- Right Panel: Article Content -->
      <main
        class="flex-1 overflow-auto right-panel h-full p-4 lg:p-8 relative"
        aria-live="polite"
      >
        <div
          v-if="articleLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-slate-50/75 dark:bg-slate-900/75"
          role="status"
        >
          <span class="sr-only">{{
            $t('HELP_CENTER_VIEWER.LOADING_ARTICLE')
          }}</span>
          <span
            class="icon i-lucide-loader animate-spin text-3xl text-blue-500"
          />
        </div>
        <div
          v-if="!selectedArticle"
          class="flex flex-col items-center justify-center h-full text-center p-8"
        >
          <div
            class="w-20 h-20 bg-slate-100 dark:bg-slate-800/50 rounded-full flex items-center justify-center mb-6 border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <span class="icon i-lucide-book-open text-3xl text-slate-400" />
          </div>
          <h2
            class="text-xl font-medium text-slate-800 dark:text-slate-200 mb-2"
          >
            {{ $t('HELP_CENTER_VIEWER.KNOWLEDGE_BASE') }}
          </h2>
          <p class="text-slate-500 dark:text-slate-400 max-w-md">
            {{ $t('HELP_CENTER_VIEWER.SELECT_ARTICLE_HINT') }}
          </p>
        </div>

        <div
          v-else
          class="article-container w-full space-y-8 animate-fade-in-up"
        >
          <div class="content-card">
            <header
              class="mb-8 text-center border-b border-slate-200 dark:border-slate-700/50 pb-8"
            >
              <span
                v-if="parsedTag(selectedArticle.title)"
                class="inline-block px-3 py-1 mb-4 text-xs font-bold uppercase tracking-wider rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
              >
                {{ parsedTag(selectedArticle.title) }}
              </span>
              <h1
                ref="articleTitle"
                tabindex="-1"
                class="text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white leading-tight mb-4"
              >
                {{ cleanTitle(selectedArticle.title) }}
              </h1>
            </header>

            <div
              v-dompurify-html="selectedArticle.content"
              class="article-content"
            />

            <div
              class="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700/50 text-center"
            >
              <h4
                class="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2"
              >
                {{ $t('HELP_CENTER_VIEWER.STILL_NEED_HELP') }}
              </h4>
              <p class="text-slate-500 dark:text-slate-400 mb-4">
                {{ $t('HELP_CENTER_VIEWER.SUPPORT_DESCRIPTION') }}
              </p>
              <button
                class="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-sm font-medium transition-colors shadow-lg shadow-blue-500/30 cursor-pointer"
                @click="openSupportChat"
              >
                <span class="icon i-lucide-message-circle" />
                {{ $t('HELP_CENTER_VIEWER.CONTACT_SUPPORT') }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Keep the reading area aligned with the dashboard neutral dark surface. */
.right-panel {
  background-color: #f8fafc; /* Light mode */
}
.dark .right-panel {
  background-color: #1f2025;
  background-image: none;
}

/* Glassmorphism Content Card */
.content-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
}

.dark .content-card {
  background: rgba(31, 32, 37, 0.94);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid #3a3b43;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out both;
}

/* Article Content HTML Styling (Premium) */
.article-content {
  line-height: 1.7;
  font-size: 1.05rem;
  color: #334155; /* Light mode text */
}

.dark .article-content {
  color: #94a3b8; /* Dark mode text */
}

.article-content :deep() {
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f93ff;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    &::before {
      content: '';
      display: block;
      width: 8px;
      height: 8px;
      background-color: #1f93ff;
      border-radius: 50%;
      box-shadow: 0 0 10px rgba(31, 147, 255, 0.4);
    }
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1.25rem;
  }

  table {
    width: 100%;
    margin: 1.5rem 0 2rem;
    border: 1px solid #cbd5e1;
    border-collapse: separate;
    border-spacing: 0;
    border-radius: 10px;
    overflow: hidden;
    font-size: 0.95rem;
  }

  th,
  td {
    padding: 0.75rem 0.9rem;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid #cbd5e1;
    border-right: 1px solid #cbd5e1;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  th {
    background: #f1f5f9;
    color: #0f172a;
    font-weight: 700;
  }

  ul {
    list-style-type: none;
    margin-top: 1rem;
    margin-bottom: 1.5rem;

    li {
      position: relative;
      padding-left: 1.5rem;
      margin-bottom: 0.75rem;

      &::before {
        content: '→';
        position: absolute;
        left: 0;
        color: #1f93ff;
        font-weight: bold;
      }
    }
  }

  ol {
    list-style-type: decimal;
    padding-left: 1.5rem;
    margin-bottom: 1.5rem;

    li {
      margin-bottom: 0.75rem;
    }
  }

  strong {
    font-weight: 600;
    color: #0f172a;
  }

  .alert-box {
    padding: 1.25rem;
    border-radius: 12px;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    margin-bottom: 1.5rem;
    color: #1e3a8a;
    display: flex;
    gap: 1rem;
    align-items: flex-start;

    p {
      margin: 0;
    }
  }

  .step-block {
    display: flex;
    margin-bottom: 1.5rem;
    background: #f8fafc;
    padding: 1rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;

    .step-number {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      margin-right: 1rem;
      box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
    }

    .step-content {
      flex-grow: 1;
      padding-top: 4px;
    }
  }
}

/* Dark Mode Overrides for Deep Content */
.dark .article-content :deep() {
  h3 {
    color: #f8fafc;
  }
  strong {
    color: #f8fafc;
  }

  .alert-box {
    background-color: rgba(30, 58, 138, 0.2);
    border-color: rgba(59, 130, 246, 0.3);
    color: #93c5fd;
  }

  .step-block {
    background: rgba(15, 23, 42, 0.5);
    border-color: #334155;
  }

  table {
    border-color: #475569;
  }

  th,
  td {
    border-color: #475569;
  }

  th {
    background: #252832;
    color: #f8fafc;
  }
}

@media (max-width: 768px) {
  .content-card {
    padding: 2rem 1.5rem;
  }
}
</style>
